import { 
  Mountain, 
  Trees, 
  CableCar, 
  UtensilsCrossed, 
  Home,
  Landmark,
  Waves,
  Globe,
  Building2,
  Castle,
  Sparkles,
  Train,
  TreePine,
  Music,
  Church,
  Coffee,
  CloudRain,
  Flower2,
  Ship,
  Droplet,
  Users,
  MapPin,
  Map,
  Calendar,
  Sun
} from 'lucide-react'

interface DestinationItem {
  name: string
  state: string
  icon: React.ReactNode
}

interface Destination {
  items: DestinationItem[]
  skip?: boolean
}

interface MonthData {
  month: string
  monthNum: number
  north: Destination
  south: Destination
  east: Destination
  central: Destination
  west: Destination
}

// Helper function to capitalize text
const capitalize = (text: string): string => {
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Helper function to parse destination string and create destination items
const parseDestination = (
  name: string,
  defaultIcon: React.ReactNode,
  stateMap: { [key: string]: string }
): DestinationItem[] => {
  const parts = name.split('/').map(part => part.trim())
  return parts.map(part => {
    const capitalized = capitalize(part)
    // Find state by matching destination name (case-insensitive)
    const partLower = capitalized.toLowerCase()
    let foundState = 'India'
    
    // Try to find matching state
    for (const [key, state] of Object.entries(stateMap)) {
      const keyLower = key.toLowerCase()
      if (partLower.includes(keyLower) || keyLower.includes(partLower.split(' ')[0])) {
        foundState = state
        break
      }
    }
    
    return {
      name: capitalized,
      state: foundState,
      icon: defaultIcon
    }
  })
}

const TravelGuide = () => {
  const currentMonth = new Date().getMonth() + 1 // 1-12

  // State mapping for destinations
  const stateMap: { [key: string]: string } = {
    'Zanskar': 'Ladakh',
    'Auli': 'Uttarakhand',
    'Wayanad': 'Kerala',
    'Munnar': 'Kerala',
    'Gangtok': 'Sikkim',
    'Tawang': 'Arunachal Pradesh',
    'Kanha': 'Madhya Pradesh',
    'Rann of Kutch': 'Gujarat',
    'Gir': 'Gujarat',
    'Spiti': 'Himachal Pradesh',
    'Kufri': 'Himachal Pradesh',
    'Hampi': 'Karnataka',
    'Vagamon': 'Kerala',
    'Indore': 'Madhya Pradesh',
    'Jaipur': 'Rajasthan',
    'Nashik': 'Maharashtra',
    'Rishikesh': 'Uttarakhand',
    'Sangla': 'Himachal Pradesh',
    'Port Blair': 'Andaman and Nicobar',
    'Varala': 'Kerala',
    'Sikkim': 'Sikkim',
    'Bandhavgarh': 'Madhya Pradesh',
    'Pushkar': 'Rajasthan',
    'Bundi': 'Rajasthan',
    'Kashmir': 'Jammu and Kashmir',
    'Barot': 'Himachal Pradesh',
    'Ooty': 'Tamil Nadu',
    'Assam': 'Assam',
    'Rinchen-Pong': 'Sikkim',
    'Ranthambhore': 'Rajasthan',
    'Leh': 'Ladakh',
    'Ladakh': 'Ladakh',
    'Jibhi': 'Himachal Pradesh',
    'Kodai': 'Tamil Nadu',
    'Kanal': 'Tamil Nadu',
    'Darjeeling': 'West Bengal',
    'Panchmarhi': 'Madhya Pradesh',
    'Mount Abu': 'Rajasthan',
    'Mcleodganj': 'Himachal Pradesh',
    'Dalhousie': 'Himachal Pradesh',
    'Coorg': 'Karnataka',
    'Valley of Flowers': 'Uttarakhand',
    'Sahyadri': 'Maharashtra',
    'Lonavala': 'Maharashtra',
    'Rakchham': 'Himachal Pradesh',
    'Chitkul': 'Himachal Pradesh',
    'Alleppey': 'Kerala',
    'Chanderi': 'Madhya Pradesh',
    'Panchgani': 'Maharashtra',
    'Alibaug': 'Maharashtra',
    'Bir': 'Himachal Pradesh',
    'Kinnaur': 'Himachal Pradesh',
    'Kochi': 'Kerala',
    'Anini': 'Arunachal Pradesh',
    'Orchha': 'Madhya Pradesh',
    'Saputara': 'Gujarat',
    'Manali': 'Himachal Pradesh',
    'Kasol': 'Himachal Pradesh',
    'Mysuru': 'Karnataka',
    'Vizag': 'Andhra Pradesh',
    'Majuli': 'Assam',
    'Kolkata': 'West Bengal',
    'Bhopal': 'Madhya Pradesh',
    'Udaipur': 'Rajasthan',
    'Vadodara': 'Gujarat',
    'Agra': 'Uttar Pradesh',
    'Amritsar': 'Punjab',
    'Chennai': 'Tamil Nadu',
    'Gokarna': 'Karnataka',
    'Shillong': 'Meghalaya',
    'Chhatrapati Sambhaji-Nagar': 'Maharashtra',
    'Goa': 'Goa',
    'Jodhpur': 'Rajasthan',
    'Mussoorie': 'Uttarakhand',
    'Shimla': 'Himachal Pradesh',
    'Pondicherry': 'Puducherry',
    'Nagaland': 'Nagaland',
    'Pench': 'Madhya Pradesh',
    'Jaisalmer': 'Rajasthan'
  }

  const travelData: MonthData[] = [
    {
      month: 'JAN',
      monthNum: 1,
      north: { items: parseDestination('Zanskar/Auli', <Mountain className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Wayanad/Munnar', <Trees className="w-5 h-5" />, stateMap) },
      east: { items: parseDestination('Gangtok/Tawang', <CableCar className="w-5 h-5" />, stateMap) },
      central: { items: [{ name: 'Kanha National Park', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: parseDestination('Rann of Kutch/Gir National Park', <TreePine className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'FEB',
      monthNum: 2,
      north: { items: parseDestination('Spiti/Kufri', <Landmark className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Hampi/Vagamon', <Castle className="w-5 h-5" />, stateMap) },
      east: { items: parseDestination('Gangtok/Tawang', <Landmark className="w-5 h-5" />, stateMap) },
      central: { items: [{ name: 'Indore', state: 'Madhya Pradesh', icon: <UtensilsCrossed className="w-5 h-5" /> }] },
      west: { items: parseDestination('Jaipur/Nashik', <Castle className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'MAR',
      monthNum: 3,
      north: { items: parseDestination('Rishikesh/Sangla', <Waves className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Port Blair/Varala', <Globe className="w-5 h-5" />, stateMap) },
      east: { items: [{ name: 'North Sikkim', state: 'Sikkim', icon: <Home className="w-5 h-5" /> }] },
      central: { items: [{ name: 'Bandhavgarh National Park', state: 'Madhya Pradesh', icon: <Landmark className="w-5 h-5" /> }] },
      west: { items: parseDestination('Pushkar/Bundi', <Sun className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'APR',
      monthNum: 4,
      north: { items: parseDestination('Kashmir/Barot', <Sparkles className="w-5 h-5" />, stateMap) },
      south: { items: [{ name: 'Ooty', state: 'Tamil Nadu', icon: <Mountain className="w-5 h-5" /> }] },
      east: { items: parseDestination('Assam/Rinchen-Pong', <Home className="w-5 h-5" />, stateMap) },
      central: { items: [{ name: 'Bandhavgarh National Park', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: [{ name: 'Ranthambhore National Park', state: 'Rajasthan', icon: <TreePine className="w-5 h-5" /> }] }
    },
    {
      month: 'MAY',
      monthNum: 5,
      north: { items: parseDestination('Leh-Ladakh/Jibhi', <Landmark className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Kodai-Kanal', <Ship className="w-5 h-5" />, stateMap) },
      east: { items: [{ name: 'Darjeeling', state: 'West Bengal', icon: <Train className="w-5 h-5" /> }] },
      central: { items: [{ name: 'Panchmarhi', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: [{ name: 'Mount Abu', state: 'Rajasthan', icon: <Music className="w-5 h-5" /> }] }
    },
    {
      month: 'JUN',
      monthNum: 6,
      north: { items: parseDestination('Mcleodganj/Dalhousie', <Church className="w-5 h-5" />, stateMap) },
      south: { items: [{ name: 'Coorg', state: 'Karnataka', icon: <Coffee className="w-5 h-5" /> }] },
      east: { items: [{ name: 'Can avoid due to heavy monsoon', state: '', icon: <CloudRain className="w-5 h-5" /> }], skip: true },
      central: { items: [{ name: 'Panchmarhi', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: [{ name: 'Mount Abu', state: 'Rajasthan', icon: <Music className="w-5 h-5" /> }] }
    },
    {
      month: 'JUL',
      monthNum: 7,
      north: { items: [{ name: 'Valley of Flowers', state: 'Uttarakhand', icon: <Flower2 className="w-5 h-5" /> }] },
      south: { items: [{ name: 'Can skip due to monsoon', state: '', icon: <CloudRain className="w-5 h-5" /> }], skip: true },
      east: { items: [{ name: 'Can skip due to heavy monsoon', state: '', icon: <CloudRain className="w-5 h-5" /> }], skip: true },
      central: { items: [{ name: 'Panchmarhi', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: parseDestination('Sahyadri Hills/Lonavala', <Mountain className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'AUG',
      monthNum: 8,
      north: { items: parseDestination('Rakchham/Chitkul', <Mountain className="w-5 h-5" />, stateMap) },
      south: { items: [{ name: 'Alleppey', state: 'Kerala', icon: <Ship className="w-5 h-5" /> }] },
      east: { items: [{ name: 'Can skip due to heavy monsoon', state: '', icon: <CloudRain className="w-5 h-5" /> }], skip: true },
      central: { items: [{ name: 'Chanderi', state: 'Madhya Pradesh', icon: <Droplet className="w-5 h-5" /> }] },
      west: { items: parseDestination('Panchgani/Alibaug', <Mountain className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'SEP',
      monthNum: 9,
      north: { items: parseDestination('Bir/Kinnaur', <Mountain className="w-5 h-5" />, stateMap) },
      south: { items: [{ name: 'Kochi', state: 'Kerala', icon: <Ship className="w-5 h-5" /> }] },
      east: { items: [{ name: 'Anini', state: 'Arunachal Pradesh', icon: <MapPin className="w-5 h-5" /> }] },
      central: { items: [{ name: 'Orchha', state: 'Madhya Pradesh', icon: <Users className="w-5 h-5" /> }] },
      west: { items: [{ name: 'Saputara Hills', state: 'Gujarat', icon: <Mountain className="w-5 h-5" /> }] }
    },
    {
      month: 'OCT',
      monthNum: 10,
      north: { items: parseDestination('Manali/Kasol', <Mountain className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Mysuru/Vizag', <MapPin className="w-5 h-5" />, stateMap) },
      east: { items: parseDestination('Majuli Island/Kolkata', <Globe className="w-5 h-5" />, stateMap) },
      central: { items: [{ name: 'Bhopal', state: 'Madhya Pradesh', icon: <Building2 className="w-5 h-5" /> }] },
      west: { items: parseDestination('Udaipur/Vadodara', <MapPin className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'NOV',
      monthNum: 11,
      north: { items: parseDestination('Agra/Amritsar', <Castle className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Chennai/Gokarna', <Waves className="w-5 h-5" />, stateMap) },
      east: { items: [{ name: 'Shillong', state: 'Meghalaya', icon: <Ship className="w-5 h-5" /> }] },
      central: { items: [{ name: 'Chhatrapati Sambhaji-Nagar', state: 'Maharashtra', icon: <MapPin className="w-5 h-5" /> }] },
      west: { items: parseDestination('Goa/Jodhpur', <Waves className="w-5 h-5" />, stateMap) }
    },
    {
      month: 'DEC',
      monthNum: 12,
      north: { items: parseDestination('Mussoorie/Shimla', <Mountain className="w-5 h-5" />, stateMap) },
      south: { items: parseDestination('Pondicherry/Goa', <Map className="w-5 h-5" />, stateMap) },
      east: { items: [{ name: 'Nagaland', state: 'Nagaland', icon: <TreePine className="w-5 h-5" /> }] },
      central: { items: [{ name: 'Pench National Park', state: 'Madhya Pradesh', icon: <TreePine className="w-5 h-5" /> }] },
      west: { items: parseDestination('Jaisalmer/Goa', <Sun className="w-5 h-5" />, stateMap) }
    }
  ]

  const regions = [
    { name: 'North', color: 'bg-blue-50 border-blue-200' },
    { name: 'South', color: 'bg-green-50 border-green-200' },
    { name: 'East', color: 'bg-purple-50 border-purple-200' },
    { name: 'Central', color: 'bg-orange-50 border-orange-200' },
    { name: 'West', color: 'bg-pink-50 border-pink-200' }
  ]

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 rounded-full">
              <Calendar className="w-8 h-8 text-primary-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Best Time to Explore India
            </h2>
          </div>
          <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto">
            Discover the perfect destinations for each month across India's diverse regions
          </p>
        </div>

        {/* Desktop Grid View */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="inline-block min-w-full">
            <table className="w-full border-collapse">
              {/* Header Row */}
              <thead>
                <tr>
                  <th className="sticky left-0 z-20 bg-white border-2 border-gray-300 p-4 font-bold text-gray-700 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Month</span>
                    </div>
                  </th>
                  {regions.map((region) => (
                    <th
                      key={region.name}
                      className={`${region.color} border-2 border-gray-300 p-4 font-bold text-gray-800 text-sm text-center`}
                    >
                      {region.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {travelData.map((data) => {
                  const isCurrentMonth = data.monthNum === currentMonth
                  return (
                    <tr
                      key={data.month}
                      className={`transition-all duration-200 ${
                        isCurrentMonth
                          ? 'bg-primary-50/50 hover:bg-primary-100/50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Month Cell */}
                      <td
                        className={`sticky left-0 z-10 border-2 border-gray-300 p-4 font-bold text-center transition-all duration-300 ${
                          isCurrentMonth
                            ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg ring-2 ring-primary-300'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className={isCurrentMonth ? 'text-lg' : ''}>{data.month}</span>
                          {isCurrentMonth && (
                            <span className="text-xs bg-white text-primary-500 px-2 py-0.5 rounded-full font-semibold animate-pulse">
                              Now
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Region Cells */}
                      <td className={`border-2 border-gray-300 p-4 ${regions[0].color}`}>
                        <DestinationCell destination={data.north} />
                      </td>
                      <td className={`border-2 border-gray-300 p-4 ${regions[1].color}`}>
                        <DestinationCell destination={data.south} />
                      </td>
                      <td className={`border-2 border-gray-300 p-4 ${regions[2].color}`}>
                        <DestinationCell destination={data.east} />
                      </td>
                      <td className={`border-2 border-gray-300 p-4 ${regions[3].color}`}>
                        <DestinationCell destination={data.central} />
                      </td>
                      <td className={`border-2 border-gray-300 p-4 ${regions[4].color}`}>
                        <DestinationCell destination={data.west} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden space-y-4">
          {travelData.map((data) => {
            const isCurrentMonth = data.monthNum === currentMonth
            return (
              <div
                key={data.month}
                className={`bg-white rounded-xl shadow-md border-2 overflow-hidden transition-all duration-200 ${
                  isCurrentMonth
                    ? 'border-primary-500 shadow-primary-200/50 ring-2 ring-primary-200'
                    : 'border-gray-200'
                }`}
              >
                {/* Month Header */}
                <div
                  className={`p-4 font-bold text-center transition-all duration-300 ${
                    isCurrentMonth
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Calendar className={`w-5 h-5 ${isCurrentMonth ? 'text-white' : 'text-gray-600'}`} />
                    <span className={`text-lg ${isCurrentMonth ? 'text-white' : ''}`}>{data.month}</span>
                    {isCurrentMonth && (
                      <span className="ml-2 text-xs bg-white text-primary-500 px-2 py-1 rounded-full font-semibold animate-pulse">
                        Current Month
                      </span>
                    )}
                  </div>
                </div>

                {/* Destinations Grid */}
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DestinationCard
                    region="North"
                    destination={data.north}
                    colorClass={regions[0].color}
                  />
                  <DestinationCard
                    region="South"
                    destination={data.south}
                    colorClass={regions[1].color}
                  />
                  <DestinationCard
                    region="East"
                    destination={data.east}
                    colorClass={regions[2].color}
                  />
                  <DestinationCard
                    region="Central"
                    destination={data.central}
                    colorClass={regions[3].color}
                  />
                  <DestinationCard
                    region="West"
                    destination={data.west}
                    colorClass={regions[4].color}
                    fullWidth
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Destination Cell Component for Desktop
const DestinationCell = ({ destination }: { destination: Destination }) => {
  if (destination.skip) {
    return (
      <div className="flex flex-col items-center gap-2 text-center min-h-[100px] justify-center p-2 opacity-60">
        <div className="p-2 rounded-lg bg-gray-200 text-gray-500">
          {destination.items[0]?.icon}
        </div>
        <p className="text-xs font-medium leading-tight text-gray-500 italic">
          {destination.items[0]?.name}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 text-center min-h-[100px] justify-center p-2">
      {destination.items.map((item, idx) => (
        <div key={idx} className="flex flex-col items-center gap-2 w-full">
          <div className="p-2 rounded-lg bg-white text-primary-600 shadow-sm hover:shadow-md hover:bg-primary-50 transition-all duration-200">
            {item.icon}
          </div>
          <div className="w-full">
            <p className="text-xs font-semibold leading-tight text-gray-800">
              {item.name}
            </p>
            {item.state && (
              <p className="text-[10px] text-primary-600 font-medium mt-0.5">
                {item.state}
              </p>
            )}
          </div>
          {idx < destination.items.length - 1 && (
            <div className="w-8 h-px bg-gray-300 my-1"></div>
          )}
        </div>
      ))}
    </div>
  )
}

// Destination Card Component for Mobile
const DestinationCard = ({
  region,
  destination,
  colorClass,
  fullWidth = false
}: {
  region: string
  destination: Destination
  colorClass: string
  fullWidth?: boolean
}) => {
  if (destination.skip) {
    return (
      <div
        className={`${colorClass} rounded-lg p-3 border-2 border-gray-200 opacity-60 ${
          fullWidth ? 'sm:col-span-2' : ''
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg flex-shrink-0 bg-gray-200 text-gray-500">
            {destination.items[0]?.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-gray-600 mb-1">{region}</div>
            <p className="text-xs font-medium leading-tight text-gray-500 italic">
              {destination.items[0]?.name}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`${colorClass} rounded-lg p-3 border-2 border-gray-200 transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${
        fullWidth ? 'sm:col-span-2' : ''
      }`}
    >
      <div className="text-xs font-bold text-gray-600 mb-2">{region}</div>
      <div className="space-y-2">
        {destination.items.map((item, idx) => (
          <div key={idx} className="flex items-start gap-3">
            <div className="p-2 rounded-lg flex-shrink-0 bg-white text-primary-600 shadow-sm hover:shadow-md transition-all duration-200">
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold leading-tight text-gray-800">
                {item.name}
              </p>
              {item.state && (
                <p className="text-[10px] text-primary-600 font-medium mt-0.5">
                  {item.state}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TravelGuide

